import React, { useState, useEffect, useRef } from 'react';
import api from '../api/axios';
import { useAuth } from '../contexts/AuthContext';

const PURPOSES = ['Interview','Vendor Bill','Business Meeting','Audit Visit','Delivery','Other'];
const EMPTY = { visitor_name:'', company_name:'', phone:'', cnic_passport:'', purpose:'Business Meeting', employee_to_meet:'', badge_number:'', notes:'' };

export default function Visitors() {
  const { user } = useAuth();
  const [visitors, setVisitors] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [editing, setEditing] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterDate, setFilterDate] = useState(new Date().toISOString().split('T')[0]);
  const [qrData, setQrData] = useState(null);
  const printRef = useRef();

  const load = async () => {
    if (!user) return;
    setLoading(true);
    try { const r = await api.get(`/api/visitors?date=${filterDate}&search=${search}`); setVisitors(r.data); }
    catch (err) { console.error('visitors:', err); setVisitors([]); }
    try { const r = await api.get('/api/employees'); setEmployees(r.data); }
    catch (err) { console.error('employees:', err); setEmployees([]); }
    setLoading(false);
  };

  useEffect(() => { if (user) load(); }, [user?.role, filterDate, search]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editing) await api.put(`/api/visitors/${editing}`, form);
      else await api.post('/api/visitors', form);
      setShowForm(false); setForm(EMPTY); setEditing(null);
      load();
    } catch (err) { alert(err.response?.data?.error || 'Failed to save visitor'); }
  };

  const handleCheckout = async (id) => {
    if (!window.confirm('Check out this visitor?')) return;
    try { await api.put(`/api/visitors/${id}/checkout`); load(); }
    catch (err) { alert('Checkout failed'); }
  };

  const handleQR = async (id) => {
    try {
      const res = await api.get(`/api/visitors/${id}/qr`);
      setQrData(res.data.qr);
    } catch { alert('QR generation failed'); }
  };

  const handleEdit = (v) => {
    setForm({ visitor_name: v.visitor_name, company_name: v.company_name||'', phone: v.phone||'',
      cnic_passport: v.cnic_passport||'', purpose: v.purpose, employee_to_meet: v.employee_to_meet||'',
      badge_number: v.badge_number||'', notes: v.notes||'' });
    setEditing(v.id); setShowForm(true);
  };

  const handlePrint = () => window.print();

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">Visitor Entry</h1>
          <p className="text-gray-500 text-sm">{visitors.length} visitor(s) on {filterDate}</p>
        </div>
        <button onClick={() => { setForm(EMPTY); setEditing(null); setShowForm(true); }}
          className="bg-blue-700 hover:bg-blue-800 text-white px-4 py-2 rounded-lg text-sm font-medium flex items-center gap-2">
          ➕ New Visitor
        </button>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl border border-gray-100 p-4 flex flex-wrap gap-3">
        <input type="date" value={filterDate} onChange={e => setFilterDate(e.target.value)}
          className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
        <input type="search" value={search} onChange={e => setSearch(e.target.value)}
          placeholder="Search name, company, CNIC..."
          className="flex-1 border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 min-w-[200px]" />
        <button onClick={handlePrint} className="border border-gray-300 rounded-lg px-3 py-2 text-sm hover:bg-gray-50">🖨️ Print</button>
      </div>

      {/* Modal Form */}
      {showForm && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-100 flex justify-between items-center">
              <h2 className="text-xl font-bold text-gray-800">{editing ? 'Edit Visitor' : 'New Visitor Entry'}</h2>
              <button onClick={() => { setShowForm(false); setEditing(null); }} className="text-gray-400 hover:text-gray-600 text-2xl">×</button>
            </div>
            <form onSubmit={handleSubmit} className="p-6 grid grid-cols-1 sm:grid-cols-2 gap-4">
              {[
                { name:'visitor_name', label:'Visitor Name *', required:true },
                { name:'company_name', label:'Company Name' },
                { name:'phone', label:'Phone' },
                { name:'cnic_passport', label:'CNIC / Passport' },
                { name:'badge_number', label:'Badge Number' },
              ].map(f => (
                <div key={f.name}>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{f.label}</label>
                  <input type="text" value={form[f.name]} onChange={e => setForm({...form,[f.name]:e.target.value})}
                    required={f.required}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
                </div>
              ))}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Purpose *</label>
                <select value={form.purpose} onChange={e => setForm({...form,purpose:e.target.value})} required
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500">
                  {PURPOSES.map(p => <option key={p}>{p}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Employee to Meet</label>
                <select value={form.employee_to_meet} onChange={e => setForm({...form,employee_to_meet:e.target.value})}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500">
                  <option value="">— Select —</option>
                  {employees.map(e => <option key={e.id} value={e.id}>{e.name} ({e.department})</option>)}
                </select>
              </div>
              <div className="sm:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
                <textarea value={form.notes} onChange={e => setForm({...form,notes:e.target.value})} rows={2}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
              </div>
              <div className="sm:col-span-2 flex gap-3 pt-2">
                <button type="submit" className="bg-blue-700 hover:bg-blue-800 text-white px-6 py-2 rounded-lg font-medium">
                  {editing ? 'Update' : 'Register Visitor'}
                </button>
                <button type="button" onClick={() => { setShowForm(false); setEditing(null); }}
                  className="border border-gray-300 px-6 py-2 rounded-lg hover:bg-gray-50">Cancel</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* QR Modal */}
      {qrData && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl p-8 text-center max-w-sm w-full shadow-2xl">
            <h3 className="text-lg font-bold mb-4">Visitor QR Pass</h3>
            <img src={qrData} alt="QR Code" className="mx-auto w-48 h-48" />
            <div className="mt-4 flex gap-3 justify-center">
              <button onClick={() => { const a=document.createElement('a');a.href=qrData;a.download='visitor-pass.png';a.click(); }}
                className="bg-blue-700 text-white px-4 py-2 rounded-lg text-sm">Download</button>
              <button onClick={() => setQrData(null)} className="border px-4 py-2 rounded-lg text-sm">Close</button>
            </div>
          </div>
        </div>
      )}

      {/* Table */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden" ref={printRef}>
        {loading ? (
          <div className="p-12 text-center text-gray-400">Loading...</div>
        ) : visitors.length === 0 ? (
          <div className="p-12 text-center text-gray-400">
            <div className="text-5xl mb-3">👥</div>
            <p>No visitors found</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 border-b border-gray-100">
                <tr>
                  {['#','Name','Company','Phone','CNIC','Purpose','Employee','In','Out','Badge','Status','Actions'].map(h => (
                    <th key={h} className="px-3 py-3 text-left text-xs font-semibold text-gray-500 uppercase">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {visitors.map((v, i) => (
                  <tr key={v.id} className="hover:bg-gray-50">
                    <td className="px-3 py-3 text-gray-400">{i+1}</td>
                    <td className="px-3 py-3 font-medium text-gray-800">{v.visitor_name}</td>
                    <td className="px-3 py-3 text-gray-600">{v.company_name||'—'}</td>
                    <td className="px-3 py-3 text-gray-600">{v.phone||'—'}</td>
                    <td className="px-3 py-3 text-gray-600">{v.cnic_passport||'—'}</td>
                    <td className="px-3 py-3">
                      <span className="px-2 py-0.5 bg-blue-50 text-blue-700 rounded text-xs">{v.purpose}</span>
                    </td>
                    <td className="px-3 py-3 text-gray-600">{v.employee_name||'—'}</td>
                    <td className="px-3 py-3 text-gray-600">{new Date(v.in_time).toLocaleTimeString('en-PK',{hour:'2-digit',minute:'2-digit'})}</td>
                    <td className="px-3 py-3 text-gray-600">{v.out_time ? new Date(v.out_time).toLocaleTimeString('en-PK',{hour:'2-digit',minute:'2-digit'}) : '—'}</td>
                    <td className="px-3 py-3 text-gray-600">{v.badge_number||'—'}</td>
                    <td className="px-3 py-3">
                      <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                        v.status==='active' ? 'bg-green-100 text-green-700' :
                        v.status==='checked_out' ? 'bg-gray-100 text-gray-500' : 'bg-yellow-100 text-yellow-700'
                      }`}>
                        {v.status==='active' ? 'Inside' : v.status==='checked_out' ? 'Out' : 'Pending'}
                      </span>
                    </td>
                    <td className="px-3 py-3">
                      <div className="flex gap-1">
                        <button onClick={() => handleEdit(v)} title="Edit" className="p-1 hover:bg-gray-100 rounded text-gray-500">✏️</button>
                        {v.status === 'active' && (
                          <button onClick={() => handleCheckout(v.id)} title="Checkout" className="p-1 hover:bg-gray-100 rounded text-gray-500">🚪</button>
                        )}
                        <button onClick={() => handleQR(v.id)} title="QR Pass" className="p-1 hover:bg-gray-100 rounded text-gray-500">📱</button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
